import { useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import GracePeriodBanner from '../../components/banners/GracePeriodBanner';
import HelpBot from '../../bots/HelpBot.jsx';


function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

export default function Layout() {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [scrolled, setScrolled]       = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const mainRef = useRef(null);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Track scroll on the main content area
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setScrolled(scrollTop > 8);
      const pct = scrollHeight <= clientHeight ? 0 : (scrollTop / (scrollHeight - clientHeight)) * 100;
      setScrollProgress(pct);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const SIDEBAR_W = '256px';

  return (
    <div
      className="min-h-screen bg-[var(--bg-primary)]"
      style={{ display: 'flex', overflow: 'hidden', height: '100dvh' }}
    >

      {/* ── SIDEBAR — fixed, full height, flex column so inner nav can scroll ── */}
      <aside
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 40,
          width: isMobile ? '272px' : SIDEBAR_W,
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: isMobile
            ? sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'
            : sidebarOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_W})`,
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          background: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border-color)',
          boxShadow: sidebarOpen ? '4px 0 32px rgba(0,0,0,0.12)' : 'none',
        }}
      >
        {/* Subtle animated shimmer stripe on the right edge */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '1px',
          height: '100%',
          background: 'linear-gradient(180deg, transparent 0%, var(--accent-primary) 40%, var(--accent-info) 60%, transparent 100%)',
          opacity: sidebarOpen ? 0.5 : 0,
          transition: 'opacity 400ms ease',
          pointerEvents: 'none',
        }} />

        <Sidebar isOpen isMobile={isMobile} onClose={() => setSidebarOpen(false)} />
      </aside>

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 30,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            transition: 'opacity 300ms ease',
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── RIGHT COLUMN — header fixed, content scrollable ───────────── */}
      <div
        style={{
          marginLeft: !isMobile && sidebarOpen ? SIDEBAR_W : 0,
          transition: 'margin-left 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          height: '100dvh',
          overflow: 'hidden',
        }}
      >
        {/* ── HEADER — sticky at the top of the right column ─────────── */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            flexShrink: 0,
            background: scrolled
              ? 'color-mix(in srgb, var(--bg-secondary) 82%, transparent)'
              : 'var(--bg-secondary)',
            backdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
            WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
            borderBottom: '1px solid var(--border-color)',
            boxShadow: scrolled
              ? '0 1px 0 color-mix(in srgb, var(--border-color) 60%, transparent), 0 4px 24px rgba(0,0,0,0.08)'
              : 'none',
            transition: 'background 250ms ease, box-shadow 250ms ease, backdrop-filter 250ms ease',
          }}
        >
          <Header
            onMenuClick={() => setSidebarOpen(o => !o)}
            sidebarOpen={sidebarOpen}
            scrolled={scrolled}
          />

          {/* Scroll progress bar */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '2px',
              width: '100%',
              transformOrigin: 'left center',
              transform: `scaleX(${scrollProgress / 100})`,
              background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-info))',
              boxShadow: '0 0 8px var(--accent-primary)',
              borderRadius: '0 2px 2px 0',
              opacity: scrollProgress > 0 && scrollProgress < 100 ? 1 : 0,
            }}
          />
        </div>

        {/* ── GRACE PERIOD BANNER — sits below header, above main ─────── */}
        <GracePeriodBanner />

        {/* ── MAIN — the only thing that scrolls ─────────────────────── */}
        <main
          ref={mainRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '24px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--border-color) transparent',
          }}
        >
          <Outlet />
        </main>
      </div>

      {/* ── HELP BOT — fixed floating widget, bottom-right corner ──────── */}
      <HelpBot />

    </div>
  );
}